import {
  Drawer,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import { DatePickerInput, TimeInput, DatesRangeValue } from "@mantine/dates"
import dayjs from "dayjs"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get"
import { Form } from "@components/typed-form"
import { DefaultError } from "@components/error-context"
import { mantineTimeToTimeOnly } from "@/utils/mantine-time-to-time-only"
import { CancelButton, SubmitButton } from "@components/Buttons"
import { DateOnly } from "@/@client/System"
import { SuspenseWithErrorBoundary } from "@components/suspense"
import {
  hasValidationErrors,
  tryGetValidationErrorsAsMantineErrors,
} from "@/utils/to-mantine-validation-errors"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { RenderSimplePerson } from "@/blocks/persons-and-users/User"
import { dateToDateOnly } from "@/utils/date-to-date-only"

export type AbsenceCellCoordinate = {
  type: "absence-cell"
  contractId: number
  shiftTypeId: number
  date: DateOnly
}

export function AddAbsenceModal(props: {
  coordinate: null | AbsenceCellCoordinate
  departmentId: number
  onAdded: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation("app_server")
  return (
    <Drawer
      position="right"
      overlayProps={{ backgroundOpacity: 0.2, blur: 0 }}
      opened={props.coordinate !== null}
      onClose={() => props.onCancel()}
      title={<Title order={3}>{t("EnterAbsence")}</Title>}
    >
      <SuspenseWithErrorBoundary>
        {props.coordinate !== null ? (
          <AddAbsenceContent
            departmentId={props.departmentId}
            coordinate={props.coordinate}
            onAdded={props.onAdded}
            onCancel={props.onCancel}
          />
        ) : null}
      </SuspenseWithErrorBoundary>
    </Drawer>
  )
}

// PersonStatusAbsence
function AddAbsenceContent({
  departmentId,
  coordinate,
  onAdded,
  onCancel,
}: {
  departmentId: number
  coordinate: AbsenceCellCoordinate
  onAdded: () => void
  onCancel: () => void
}) {
  const { data: availableAbsenceShiftTypes } = useSuspenseQueryGet(
    "/api/ShiftType/GetRequestableAbsenceShiftTypesForDepartment",
    {
      departmentId,
    }
  )
  const shiftType = availableAbsenceShiftTypes.find(
    (v) => v.shiftTypeId == coordinate.shiftTypeId
  )
  const { t } = useTranslation("app_server")
  return (
    <Form
      url="/api/Orchestra/AddAbsence"
      onSuccess={() => onAdded()}
      onError={(e, _, __, f) => {
        if (e instanceof DefaultError && hasValidationErrors(e)) {
          const validationErrors = tryGetValidationErrorsAsMantineErrors(e, t)
          if (validationErrors) {
            f.setErrors(validationErrors)
          }
        }
        showErrorNotification(e)
      }}
      initialValues={{
        shiftTypeId: coordinate.shiftTypeId.toString(),
        dateRange: [
          dayjs(coordinate.date).set("h", 0).set("m", 0).set("s", 0).toDate(),
          dayjs(coordinate.date).set("h", 0).set("m", 0).set("s", 0).toDate(),
        ] as DatesRangeValue,
        startTime: shiftType?.startTimeDefault1 ?? null,
        endTime: shiftType?.endTimeDefault1 ?? null,
        note: null as any as string,
      }}
      transform={(v) => {
        const startDate = v.dateRange[0]
          ? dateToDateOnly(new Date(v.dateRange[0]))
          : null
        const endDate = v.dateRange[1]
          ? dateToDateOnly(new Date(v.dateRange[1]))
          : null

        return {
          contractId: coordinate.contractId,
          shiftTypeId: parseInt(v.shiftTypeId),
          startDate: startDate!,
          endDate: endDate!,
          note: v.note,
          startTime: v.startTime ? mantineTimeToTimeOnly(v.startTime) : null,
          endTime: v.endTime ? mantineTimeToTimeOnly(v.endTime) : null,
          departmentId,
        }
      }}
    >
      {(f, _, m) => {
        const errors = Object.keys(f.errors).map((k) => f.errors[k])
        return (
          <Stack>
            <Text fw={700}>
              <RenderSimplePerson suspense contractId={coordinate.contractId} />
            </Text>
            <DatePickerInput
              type="range"
              label={t("TimePeriod")}
              placeholder={t("TimePeriod")}
              {...f.getInputProps(_.dateRange._PATH_)}
            />
            <Group>
              <TimeInput
                label={t("StartTime")}
                {...f.getInputProps(_.startTime._PATH_)}
              />
              <TimeInput
                label={t("EndTime")}
                {...f.getInputProps(_.endTime._PATH_)}
              />
            </Group>
            <Select
              label={t("ReasonForAbsence")}
              data={availableAbsenceShiftTypes.map((v) => ({
                label: v.name,
                value: v.shiftTypeId.toString(),
              }))}
              {...f.getInputProps(_.shiftTypeId._PATH_)}
            />
            <Textarea label={t("Note")} {...f.getInputProps(_.note._PATH_)} />

            <Group justify="space-between" mt="md">
              <CancelButton onClick={onCancel} />
              <SubmitButton disabled={errors.length > 0} loading={m.isPending}>
                {t("EnterAbsence")}
              </SubmitButton>
            </Group>
          </Stack>
        )
      }}
    </Form>
  )
}
